/**
 *  heic.js
 *  @description: fotos HEIC/HEIF (iPhone y algunos Android) a JPEG en el
 *  navegador. Chrome y Firefox no las muestran y la API solo acepta JPEG y PNG.
 *  Se detectan por el contenido, no por el nombre: al descargarlas a veces
 *  llegan como ".mp4" porque comparten contenedor con el video (caja "ftyp").
 */

const HEIC_BRANDS = ['heic', 'heix', 'hevc', 'hevx', 'heim', 'heis', 'mif1', 'msf1'];
const MAX_SIDE = 2560;
const QUALITY = 0.85;

export async function isHeic(file) {
   const head = new Uint8Array(await file.slice(0, 12).arrayBuffer());
   const text = String.fromCharCode(...head);
   return text.slice(4, 8) === 'ftyp' && HEIC_BRANDS.includes(text.slice(8, 12));
}

// toJpeg convierte y, si la foto es muy grande, la reduce a MAX_SIDE por lado.
export async function toJpeg(file) {
   const { default: heic2any } = await import('heic2any');
   const converted = await heic2any({ blob: file, toType: 'image/jpeg', quality: QUALITY });
   const blob = await shrink(Array.isArray(converted) ? converted[0] : converted);
   const name = file.name.replace(/(\.(heic|heif))?(\.[a-z0-9]+)?$/i, '') + '.jpg';
   return new File([blob], name, { type: 'image/jpeg' });
}

// fitImage reduce a JPEG una foto que pasa del límite (las de celular suelen
// medir 3-6 MB). Si no se puede leer, regresa el archivo tal cual.
export async function fitImage(file) {
   try {
      const blob = await shrink(file, true);
      if (blob === file) return file;
      const name = file.name.replace(/\.[a-z0-9]+$/i, '') + '.jpg';
      return new File([blob], name, { type: 'image/jpeg' });
   } catch (err) {
      return file;
   }
}

// shrink reduce a MAX_SIDE por lado; con force vuelve a codificar aunque ya
// quepa (un PNG o un JPEG de poca compresión puede pasar de 2 MB igual).
async function shrink(blob, force = false) {
   const img = await createImageBitmap(blob);
   const scale = Math.min(1, MAX_SIDE / Math.max(img.width, img.height));
   if (scale === 1 && !force) return blob;
   const canvas = document.createElement('canvas');
   canvas.width = Math.round(img.width * scale);
   canvas.height = Math.round(img.height * scale);
   canvas.getContext('2d').drawImage(img, 0, 0, canvas.width, canvas.height);
   return new Promise((resolve) => canvas.toBlob(resolve, 'image/jpeg', QUALITY));
}
